import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Box } from "@mui/material";
import moment from "moment";

import { ActionButton, CardLayout, InputField, SelectField } from "../v3";
import { AdminService, HumanResourceService, ProjectService } from "../../services";
import { getFilteredOptions, selectAllOption, selectAllUsers } from "../../utils";

export const EmployeeReportForm = ({ reportType = "summary", handleClear = () => {}, onGenerate = () => {} }) => {
  const [selectedEmployees, setSelectedEmployees] = useState([selectAllOption]);
  const [selectedProjects, setSelectedProjects] = useState([selectAllOption]);
  const [selectedUsers, setSelectedUsers] = useState([selectAllUsers]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [reportDetails, setReportDetails] = useState({
    employeeIds: null,
    accountIds: null,
    projectIds: null,
    createdByIds: null,
    fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });

  useEffect(() => {
    handleClear();
    setLoading(false);
  }, [reportType]);

  const updateDetailsState = (event) => {
    handleClear();
    setLoading(false);
    const { name, value } = event.target;
    setReportDetails({ ...reportDetails, [name]: value });
    if (name === "fromDate") setErrors((prev) => ({ ...prev, fromDate: "" }));
    else if (name === "toDate") setErrors((prev) => ({ ...prev, toDate: "" }));
  };

  const handleEmployeeChange = (employees) => {
    handleClear();
    setLoading(false);
    const allSelected = employees.some((employee) => employee.id === "all");

    if (allSelected) {
      setSelectedEmployees([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, employeeIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = employees.filter((employee) => employee.id !== "all");
      setSelectedEmployees(filtered);
      setReportDetails((prev) => ({
        ...prev,
        employeeIds: filtered.map((employee) => employee.id),
        accountIds: filtered.map((employee) => employee.accountId),
      }));
    }

    setErrors((prev) => ({ ...prev, employeeIds: "" }));
  };

  const handleProjectChange = (projects) => {
    handleClear();
    setLoading(false);
    const allSelected = projects.some((project) => project.id === "all");

    if (allSelected) {
      setSelectedProjects([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, projectIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = projects.filter((project) => project.id !== "all");
      setSelectedProjects(filtered);
      setReportDetails((prev) => ({ ...prev, projectIds: filtered.map((project) => project.id) }));
    }

    setErrors((prev) => ({ ...prev, projectIds: "" }));
  };

  const handleUserChange = (users) => {
    handleClear();
    setLoading(false);
    const allSelected = users.some((user) => user.id === "all");

    if (allSelected) {
      setSelectedUsers([selectAllUsers]);
      setReportDetails((prev) => ({ ...prev, createdByIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = users.filter((user) => user.id !== "all");
      setSelectedUsers(filtered);
      setReportDetails((prev) => ({ ...prev, createdByIds: filtered.map((user) => user.id) }));
    }

    setErrors((prev) => ({ ...prev, createdByIds: "" }));
  };

  const handleGenerateReport = async () => {
    setErrors({});
    try {
      setLoading(true);
      const isoFromDate = moment(reportDetails.fromDate).startOf("day").toISOString();
      const isoToDate = moment(reportDetails.toDate).endOf("day").toISOString();

      const payload = {
        ...reportDetails,
        fromDate: isoFromDate,
        toDate: isoToDate,
        reportType,
      };

      onGenerate?.(payload, {
        onError: () => setLoading(false),
      });
    } catch (error) {
      setLoading(false);
      toast.error("Failed to generate report");
      console.error("🚀 ~ handleGenerateReport ~ error:", error);
    }
  };

  return (
    <>
      <CardLayout>
        <Box mt={1}>
          <Box display="flex" gap={2}>
            <SelectField
              label="Employee"
              value={selectedEmployees}
              getData={HumanResourceService.getEmployees}
              getDataStaticFilters={{ type: "EMPLOYEE", sortBy: "name", sortOrder: "asc" }}
              required
              multiSelect
              error={!!errors.employeeIds}
              helperText={errors.employeeIds}
              onChange={handleEmployeeChange}
              optionFilter={(options) => getFilteredOptions(options, selectedEmployees, selectAllOption)}
              style={{ width: "100%" }}
            />
          </Box>
          <Box display="flex" gap={2}>
            <SelectField
              label="Project"
              value={selectedProjects}
              getData={ProjectService.getProjectList}
              getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
              required
              multiSelect
              error={!!errors.projectIds}
              helperText={errors.projectIds}
              onChange={handleProjectChange}
              optionFilter={(options) => getFilteredOptions(options, selectedProjects, selectAllOption)}
              style={{ width: "100%" }}
            />
            <SelectField
              label="Entry By"
              value={selectedUsers}
              getData={AdminService.getUserList}
              getDataStaticFilters={{ type: "USER" }}
              optionValueFields={["firstName", "lastName"]}
              required
              multiSelect
              error={!!errors.createdByIds}
              helperText={errors.createdByIds}
              onChange={handleUserChange}
              optionFilter={(options) => getFilteredOptions(options, selectedUsers, selectAllUsers)}
              style={{ width: "100%" }}
            />
          </Box>
          <Box display="flex" gap={2}>
            <InputField
              required
              type="date"
              label="From Date"
              name="fromDate"
              value={reportDetails.fromDate}
              onChange={updateDetailsState}
              error={!!errors.fromDate}
              helperText={errors.fromDate}
              fullWidth
            />
            <InputField
              required
              type="date"
              label="To Date"
              name="toDate"
              value={reportDetails.toDate}
              onChange={updateDetailsState}
              error={!!errors.toDate}
              helperText={errors.toDate}
              fullWidth
            />
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <ActionButton buttonText="Generate" color="primary" disabled={loading} onButtonClick={handleGenerateReport} />
        </Box>
      </CardLayout>
    </>
  );
};
